import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import { eventoFromSlot, eventoLabel } from "../../utils/chatbotSlotEvent";
import { isSlotFaqIaMode, isSlotAgenteIaMode } from "../../utils/chatbotSlotFaq";

function formatBotoes(botoes) {
  if (!botoes) return [];
  if (Array.isArray(botoes)) {
    return botoes.map((b) => {
      if (b && typeof b === "object") return String(b.label || b.url || "").trim();
      return String(b).trim();
    }).filter(Boolean);
  }
  return String(botoes)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function actionLabels(slot) {
  const list = Array.isArray(slot?.acoes) && slot.acoes.length ? slot.acoes : null;
  if (list) {
    return list.map((a) => {
      const t = Number(a.tipo_acao) || 0;
      const id = Number(a.id_acao) || 0;
      if (t === 4) return "Alterar a Solicitação";
      if (t === 3) return `Regra #${id}`;
      if (t === 2) return `Ir para #${id}`;
      return `${t}-${id}`;
    });
  }
  const single = actionLabel(slot);
  return single ? [single] : [];
}

function actionLabel(slot) {
  const t = Number(slot?.tipo_acao) || 0;
  const a = Number(slot?.id_acao) || 0;
  if (!t && !a) return null;
  if (t === 4) return "Alterar a Solicitação";
  if (t === 3) return `Regra de automação #${a}`;
  if (t === 2) return `Ir para passo #${a}`;
  return `Ação ${t}-${a}`;
}

export function truncateText(text, max = 320) {
  const s = String(text || "").trim();
  if (!s) return "";
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

export default function ChatbotSlotTooltipContent({ slot }) {
  if (!slot) return null;

  const identificador = String(slot.identificador || "").trim();
  const nome = String(slot.nome || "").trim();
  const resposta = truncateText(slot.resposta, 400);
  const botoes = formatBotoes(slot.botoes);
  const acoes = actionLabels(slot);
  const isExcecao = identificador === "Exceção";
  const isGlobal = !!slot.global;
  const isFaqIa = isSlotFaqIaMode(slot);
  const isAgentIa = isSlotAgenteIaMode(slot);
  const evento = eventoFromSlot(slot);

  return (
    <Box sx={{ maxWidth: 360, py: 0.25 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
        {identificador || nome || `Passo #${slot.id}`}
      </Typography>

      {nome && nome !== identificador ? (
        <Typography variant="caption" display="block" sx={{ opacity: 0.9, mb: 0.5 }}>
          Nome: {nome}
        </Typography>
      ) : null}

      {isGlobal ? (
        <Typography variant="caption" display="block" sx={{ opacity: 0.9, mb: 0.5 }}>
          Global · {eventoLabel(evento)}
        </Typography>
      ) : null}

      {isFaqIa ? (
        <Typography variant="caption" display="block" sx={{ opacity: 0.9, mb: 0.5, color: "info.main" }}>
          Agente FAQ (IA + base de conhecimento)
        </Typography>
      ) : null}

      {isAgentIa ? (
        <Typography variant="caption" display="block" sx={{ opacity: 0.9, mb: 0.5, color: "secondary.main" }}>
          Agente de IA: {String(slot.agente_ia?.agent_id || "").slice(0, 8) || "—"}
          {slot.agente_ia?.pergunta ? ` · ${truncateText(slot.agente_ia.pergunta, 80)}` : ""}
        </Typography>
      ) : null}

      {isExcecao ? (
        <Typography variant="caption" display="block" sx={{ opacity: 0.85, mb: 0.75 }}>
          Acionado quando nenhuma opção deste nível for válida.
        </Typography>
      ) : null}

      {resposta ? (
        <>
          <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.25 }}>
            Mensagem
          </Typography>
          <Typography
            variant="caption"
            component="div"
            sx={{
              opacity: 0.95,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.45,
              maxHeight: 160,
              overflow: "auto",
            }}
          >
            {resposta}
          </Typography>
        </>
      ) : (
        <Typography variant="caption" display="block" sx={{ opacity: 0.75, fontStyle: "italic" }}>
          Sem mensagem configurada.
        </Typography>
      )}

      {(botoes.length > 0 || acoes.length > 0) && <Divider sx={{ my: 0.75, borderColor: "rgba(255,255,255,0.2)" }} />}

      {botoes.length > 0 ? (
        <Typography variant="caption" display="block" sx={{ opacity: 0.9 }}>
          Botões: {botoes.slice(0, 6).join(" · ")}
          {botoes.length > 6 ? ` (+${botoes.length - 6})` : ""}
        </Typography>
      ) : null}

      {acoes.length > 0 ? (
        <Typography variant="caption" display="block" sx={{ opacity: 0.9, mt: botoes.length ? 0.25 : 0 }}>
          Ações: {acoes.join(" → ")}
        </Typography>
      ) : null}
    </Box>
  );
}
